import React, { useCallback, useEffect, useState } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { Dropdown, Label, TextField, DatePicker, Stack, ChoiceGroup, DefaultButton, PrimaryButton } from '@fluentui/react';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { getTimezoneI18nString } from '@bingads-webui-campaign-react/help';
import { formatCountdown, DayPickerStrings, defaultCountdownDays } from './utils';
import { getSupportedLanguages, getLanguageCode } from './countdown-language';

const today = new Date(Date.now());
const getTimeZoneText = (timeZoneId, i18n) => {
  const text = i18n.getString(getTimezoneI18nString(timeZoneId));
  return _.isString(text) ? text.replace(/\{0\}\s*\{1\}\s*/, '') : null;
};
const getLocalDate = (date, fromUTC = true) => {
  if (_.isNull(date) || _.isUndefined(date)) {
    return null;
  }

  const parsed = new Date(date);
  if (fromUTC) {
    const offset = parsed.getTimezoneOffset();
    // eslint-disable-next-line no-mixed-operators
    return new Date(parsed.getTime() + offset * 60000);
  }

  return parsed;
};

const testDatePattern = (adType, value) => {
  const datePattern = adType === 'ResponsiveSearchAd' || adType === 'ResponsiveAd' ?
    /^[0-9]{4}-([1-9]|0[1-9]|1[0-2])-([1-9]|0[1-9]|[1-2][0-9]|3[0-1])$/ :
    /^[0-9]{4}\/([1-9]|0[1-9]|1[0-2])\/([1-9]|0[1-9]|[1-2][0-9]|3[0-1])$/;
  return datePattern.test(value);
};

export function CountdownEditor(props) {
  const {
    i18n, adType, target, options, updateAndCloseCallback, timeZone, permissions = {},
  } = props;

  const [date, setDate] = useState(options.date ? getLocalDate(options.date, adType === 'ResponsiveSearchAd' || adType === 'ResponsiveAd') : '');
  const [time, setTime] = useState(options.time || '');
  const [days, setDays] = useState(options.days || defaultCountdownDays);
  const [countdownType, setCountdownType] = useState(options.type);
  const [language, setLanguage] = useState(options.language && options.language.displayName);
  const [supportedLanguages] = useState(() => _.map(getSupportedLanguages(i18n, permissions), lang => ({ key: lang, text: lang })));
  const [isTimeValid, setIsTimeValid] = useState(true);
  const [isDateValid, setIsDateValid] = useState(true);
  const [isDaysValid, setIsDaysValid] = useState(true);

  const onChangeTime = useCallback((event, value) => {
    setTime(value);
  }, []);
  const onChangeDays = useCallback((event, value) => {
    setDays(value);
  }, []);
  const onChangeCountdownType = useCallback((event, option) => {
    setCountdownType(option.key);
  }, []);
  const onChangeLanguage = useCallback((event, value) => {
    setLanguage(value.key);
  }, []);

  const getDaysErrorMessage = (value) => {
    setIsDaysValid(false);
    if (_.isEmpty(value)) return i18n.getString(_TL_('Number of countdown days is required.'));
    if (!Number.isInteger(Number(value)) || !/^\d*$/.test(value)) return i18n.getString(_TL_('Countdown days must be an integer value.'));
    if (!(value >= 1 && value <= 999)) return i18n.getString(_TL_('Countdown days must be between 1 and 999.'));
    setIsDaysValid(true);
    return '';
  };

  const getTimeErrorMessage = (value) => {
    setIsTimeValid(false);
    if (!/^(?:((?:[0-1][0-9]|2[0-3]):(?:[0-5][0-9]):(?:[0-5][0-9]))|)$/.test(value)) return i18n.getString(_TL_('Enter countdown end time in HH:mm:ss format or leave it blank.'));
    setIsTimeValid(true);
    return '';
  };

  const getDateErrorMessage = (value) => {
    setIsDateValid(false);
    if (_.isEmpty(value)) return i18n.getString(_TL_('Countdown end date is required.'));
    if (!testDatePattern(adType, value)) {
      return i18n.getString(adType === 'ResponsiveSearchAd' || adType === 'ResponsiveAd' ?
        _TL_('Enter a future date in yyyy-MM-dd format.') :
        _TL_('Enter a future date in yyyy/MM/dd format.'));
    }
    setIsDateValid(true);
    return '';
  };

  const timeZoneOptions = [
    { key: 'global_countdown', text: `${i18n.getString(_TL_('Account time zone'))} ${timeZone ? getTimeZoneText(timeZone, i18n) : ''}` },
    { key: 'countdown', text: `${i18n.getString(_TL_("Ad viewer's time zone"))} ` },
  ];

  const onFormatDate = rawDate =>
    i18n.formatDate(rawDate, { raw: adType === 'ResponsiveSearchAd' || adType === 'ResponsiveAd' ? 'yyyy-MM-dd' : 'yyyy/MM/dd' });

  const set = () => {
    if (!isTimeValid || !isDateValid || !isDaysValid) return;

    const prefix = target.value.substring(0, options.startIndex);
    const suffix = target.value.substring(options.endIndex);

    const newValue = formatCountdown(
      prefix,
      countdownType,
      onFormatDate(date),
      time.length === 0 ? '00:00:00' : time,
      getLanguageCode(language, i18n, permissions),
      days,
      suffix,
      adType === 'ResponsiveSearchAd' || adType === 'ResponsiveAd'
    );

    const cursorPosition = newValue.length - suffix.length;
    updateAndCloseCallback({ newValue, cursorPosition, isSelectedByUser: true });
  };

  const cancel = () => {
    updateAndCloseCallback({ isCancel: true });
  };

  useEffect(() => {
    setDate(options.date ? getLocalDate(options.date, adType === 'ResponsiveSearchAd' || adType === 'ResponsiveAd') : '');
    setTime(options.time || '');
    setDays(options.days || defaultCountdownDays);
  }, [adType, options]);

  return (
    <div className="countdownEditor">
      <form>
        <Label>
          {i18n.getString(_TL_('Countdown ends'))}
          <HelpPopupInline
            topic="pop_BA_NewAdCreate_Countdown_End"
            ariaLabel={i18n.getString(_TL_('Click to learn more about Syntax'))}
          />
        </Label>
        <Stack horizontal gap={12}>
          <DatePicker
            strings={DayPickerStrings(i18n)}
            ariaLabel="Select the countdown ends date."
            value={date}
            minDate={today}
            onSelectDate={setDate}
            formatDate={onFormatDate}
            showMonthPickerAsOverlay
            textField={{
              onGetErrorMessage: getDateErrorMessage,
              }
            }
            allowTextInput
          />
          <TextField
            className="countdown-time"
            value={time}
            onChange={onChangeTime}
            validateOnFocusOut
            onGetErrorMessage={getTimeErrorMessage}
            placeholder="HH:mm:ss"
          />
          <span>{i18n.getString(_TL_('(24-hour time)'))}</span>
        </Stack>
        <Label>
          {i18n.getString(_TL_('Countdown starts'))}
          <HelpPopupInline
            topic="pop_BA_NewAdCreate_Countdown_Start"
            ariaLabel={i18n.getString(_TL_('Click to learn more about Syntax'))}
          />
        </Label>
        <Stack horizontal gap={12}>
          <TextField
            className="countdown-days"
            value={days}
            onChange={onChangeDays}
            validateOnFocusOut
            onGetErrorMessage={getDaysErrorMessage}
          />
          <span>{i18n.getString(_TL_('day(s) before countdown end date'))}</span>
        </Stack>
        <Label>
          {i18n.getString(_TL_('Time zone'))}
          <HelpPopupInline
            topic="pop_BA_NewAdCreate_Countdown_TimeZone"
            ariaLabel={i18n.getString(_TL_('Click to learn more about Syntax'))}
          />
        </Label>
        <Stack horizontal gap={12}>
          <ChoiceGroup
            className="countdown-timezone"
            options={timeZoneOptions}
            selectedKey={countdownType}
            onChange={onChangeCountdownType}
          />
        </Stack>
        {adType !== 'ResponsiveSearchAd' && adType !== 'ResponsiveAd' && (
          <>
            <Label>
              {i18n.getString(_TL_('Language'))}
              <HelpPopupInline
                topic="pop_BA_NewAdCreate_Countdown_Language"
                ariaLabel={i18n.getString(_TL_('Click to learn more about Language'))}
              />
            </Label>
            <Stack horizontal gap={12}>
              <Dropdown
                className="language-dropdown"
                placeholder={i18n.getString(_TL_('Select a language'))}
                options={supportedLanguages}
                selectedKey={language}
                onChange={onChangeLanguage}
              />
            </Stack>
          </>
        )}
        <Stack horizontal gap={12}>
          <PrimaryButton className="save" text={i18n.getString(_TL_('Set'))} onClick={set} />
          <DefaultButton className="cancel" text={i18n.getString(_TL_('Cancel'))} onClick={cancel} />
        </Stack>
      </form>
    </div>
  );
}

CountdownEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    formatDate: PropTypes.func.isRequired,
  }).isRequired,
  adType: PropTypes.string,
  target: PropTypes.shape({
    value: PropTypes.shape({
      substring: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
  options: PropTypes.shape({
    date: PropTypes.string,
    time: PropTypes.string,
    days: PropTypes.string,
    language: PropTypes.string,
    type: PropTypes.string,
    startIndex: PropTypes.number.isRequired,
    endIndex: PropTypes.number.isRequired,
  }).isRequired,
  updateAndCloseCallback: PropTypes.func.isRequired,
  permissions: PropTypes.shape({}).isRequired,
  timeZone: PropTypes.func.isRequired,
};

CountdownEditor.defaultProps = {
  adType: 'ResponsiveSearchAd',
};
